import React from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { toGregorian } from "jalaali-js";
import { FiX } from "react-icons/fi";
import { CustomIcon } from "../components/CustomIcon";
import { CrudPage } from "../components/CrudPage";
import { RecordDetailsModal } from "../components/RecordDetailsModal";
import { FundSelectionCard } from "../components/funds/FundSelectionCard";
import {
  PersianDatePicker,
  type PersianDayValue,
} from "../components/common/PersianDatePicker";
import {
  formatFundCurrency,
} from "../components/funds/fundCardUtils";
import { useAuth } from "../context/AuthContext";
import { tadbir } from "../services/api";
import { TadbirFund, useFunds } from "../hooks/useFunds";
import {
  getUserIdentifier,
  getUserIdentifierLabel,
} from "../utils/userIdentity";

const pageSize = 20;

const turnoverColumns = [
  {
    header: "تاریخ سند",
    accessor: "voucherDate",
    render: (item: any) =>
      item.voucherDate
        ? new Date(item.voucherDate).toLocaleDateString("fa-IR")
        : "—",
  },
  { header: "شرح", accessor: "description" },
  {
    header: "بستانکار",
    accessor: "creditor",
    render: (item: any) => Number(item.creditor || 0).toLocaleString("fa-IR"),
  },
  {
    header: "بدهکار",
    accessor: "debitor",
    render: (item: any) => Number(item.debitor || 0).toLocaleString("fa-IR"),
  },
  {
    header: "مانده حساب",
    accessor: "accountBalance",
    render: (item: any) =>
      Number(item.accountBalance || 0).toLocaleString("fa-IR"),
  },
  // {
  //   header: "سبد",
  //   accessor: "basketName",
  //   render: (item: any) => item.basketName || "—",
  // },
];

const toGregorianDate = (value: PersianDayValue) => {
  if (!value) return "";

  const gregorianDate = toGregorian(value.year, value.month, value.day);

  return [
    gregorianDate.gy,
    String(gregorianDate.gm).padStart(2, "0"),
    String(gregorianDate.gd).padStart(2, "0"),
  ].join("-");
};

const formatCompactPersianDate = (value: PersianDayValue) => {
  if (!value) return "—";

  return [value.year, value.month, value.day].join("/");
};

const toGregorianNativeDate = (value: PersianDayValue) => {
  if (!value) return null;

  const gregorianDate = toGregorian(value.year, value.month, value.day);
  return new Date(gregorianDate.gy, gregorianDate.gm - 1, gregorianDate.gd);
};

const findFundBySeo = (funds: TadbirFund[], selectedFund: string) =>
  funds.find(
    (fund) =>
      String(fund.nav?.seoRegisterNo || fund.fundRegisterCode) === selectedFund,
  );

export const TurnoverAccountPage: React.FC = () => {
  const { user } = useAuth();
  const identifier = getUserIdentifier(user);
  const identifierLabel = getUserIdentifierLabel(user);
  const { data: fundsData, isLoading: isFundsLoading } = useFunds();
  const funds = React.useMemo(() => fundsData?.items || [], [fundsData]);
  const filterModal = useDisclosure();
  const toast = useToast();
  const cardBg = useColorModeValue("white", "dark.50");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  const [selectedFund, setSelectedFund] = React.useState("");
  const [fromDate, setFromDate] = React.useState<PersianDayValue>(null);
  const [toDate, setToDate] = React.useState<PersianDayValue>(null);
  const [draftFromDate, setDraftFromDate] =
    React.useState<PersianDayValue>(null);
  const [draftToDate, setDraftToDate] = React.useState<PersianDayValue>(null);
  const [selectedItem, setSelectedItem] = React.useState<any>(null);
  const [turnoverRows, setTurnoverRows] = React.useState<any[]>([]);

  const appliedFromDate = React.useMemo(
    () => (fromDate ? toGregorianDate(fromDate) : undefined),
    [fromDate],
  );
  const appliedToDate = React.useMemo(
    () => (toDate ? toGregorianDate(toDate) : undefined),
    [toDate],
  );

  React.useEffect(() => {
    if (filterModal.isOpen) {
      setDraftFromDate(fromDate);
      setDraftToDate(toDate);
    }
  }, [filterModal.isOpen, fromDate, toDate]);

  React.useEffect(() => {
    if (draftFromDate && draftToDate) {
      const fromValue = toGregorianNativeDate(draftFromDate);
      const toValue = toGregorianNativeDate(draftToDate);
      if (fromValue && toValue && fromValue > toValue) {
        setDraftToDate(draftFromDate);
      }
    }
  }, [draftFromDate, draftToDate]);

  React.useEffect(() => {
    if (!selectedFund && funds.length > 0) {
      const firstFund = funds[0];
      setSelectedFund(
        String(firstFund.nav?.seoRegisterNo || firstFund.fundRegisterCode),
      );
    }
  }, [funds, selectedFund]);

  if (!identifier) {
    return (
      <Alert status="warning" borderRadius="lg">
        <AlertIcon />
        {identifierLabel} کاربر برای دریافت گردش حساب یافت نشد.
      </Alert>
    );
  }

  if (isFundsLoading) {
    return (
      <VStack
        w="100%"
        h="100dvh"
        py={10}
        spacing={4}
        align="center"
        justify="center"
      >
        <Spinner size="xl" color="brand.500" />
        <Text color={mutedColor}>در حال دریافت صندوق‌ها...</Text>
      </VStack>
    );
  }

  const applyDateFilters = () => {
    const hasOnlyOneDate =
      (draftFromDate && !draftToDate) || (!draftFromDate && draftToDate);

    if (hasOnlyOneDate) {
      toast({
        title: "بازه تاریخ ناقص است.",
        description:
          "برای اعمال فیلتر تاریخ، هر دو تاریخ شروع و پایان را وارد کنید.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setFromDate(draftFromDate);
    setToDate(draftToDate);
    filterModal.onClose();
  };

  const clearDateFilters = () => {
    setDraftFromDate(null);
    setDraftToDate(null);
    setFromDate(null);
    setToDate(null);
    filterModal.onClose();
  };

  const onClickClearFilters = (e: React.MouseEvent<HTMLElement>) => {
    if (!fromDate && !toDate) {
      filterModal.onOpen();
    } else {
      e.preventDefault();
      e.stopPropagation();
      clearDateFilters();
    }
  };

  const totalCreditor = turnoverRows.reduce(
    (sum, item) => sum + Number(item.creditor || 0),
    0,
  );
  const totalDebitor = turnoverRows.reduce(
    (sum, item) => sum + Number(item.debitor || 0),
    0,
  );
  const selectedFundData = findFundBySeo(funds, selectedFund);
  const isDateFiltered = !!fromDate && !!toDate;
  const canApplyDateFilters = !!draftFromDate && !!draftToDate;

  const headerContent = (
    <VStack align="stretch" spacing={5}>
      <Box borderRadius="3xl" bg={cardBg}>
        <VStack align="stretch" spacing={5}>
          <HStack
            justify="space-between"
            align={{ base: "start", md: "center" }}
            flexWrap="wrap"
            spacing={3}
          >
            <VStack align="start" spacing={1}>
              <Heading size="md">دسترسی سریع صندوق‌ها</Heading>
              <Text color={mutedColor} fontSize="sm">
                گردش حساب صندوق فعال را ببینید و در صورت نیاز بازه تاریخ را
                محدود کنید
              </Text>
            </VStack>

            <HStack spacing={3} flexWrap="wrap">
              <HStack spacing={0} dir="rtl">
                <Button
                  as="button"
                  type="button"
                  onClick={filterModal.onOpen}
                  size="md"
                  leftIcon={
                    isDateFiltered ? (
                      <Box
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        borderRadius="full"
                        p={1}
                        bg={"red.500"}
                        _hover={{
                          bg: "red.500",
                          color: "white",
                        }}
                        transition="all .2s"
                        onClick={onClickClearFilters}
                      >
                        <CustomIcon icon={FiX} size={14} />
                      </Box>
                    ) : undefined
                  }
                  rightIcon={<ChevronDownIcon boxSize={5} />}
                  borderRadius={"full"}
                  fontSize="sm"
                  fontWeight="semibold"
                  colorScheme={isDateFiltered ? "green" : "gray"}
                  variant={isDateFiltered ? "solid" : "outline"}
                  borderWidth="1px"
                >
                  {isDateFiltered
                    ? `بازه تاریخ: ${formatCompactPersianDate(fromDate)} تا ${formatCompactPersianDate(toDate)}`
                    : "فیلتر تاریخ غیرفعال"}
                </Button>
              </HStack>
              <Badge
                colorScheme="green"
                variant="subtle"
                px={3}
                py={2}
                borderRadius="full"
                fontSize="sm"
              >
                جمع بستانکار: {formatFundCurrency(totalCreditor)}
              </Badge>
              <Badge
                colorScheme="red"
                variant="subtle"
                px={3}
                py={2}
                borderRadius="full"
                fontSize="sm"
              >
                جمع بدهکار: {formatFundCurrency(totalDebitor)}
              </Badge>
            </HStack>
          </HStack>

          <FundSelectionCard
            fundData={funds}
            value={selectedFund}
            onChange={setSelectedFund}
          />
        </VStack>
      </Box>
    </VStack>
  );

  const footerContent = (
    <>
      <Modal
        isOpen={filterModal.isOpen}
        onClose={filterModal.onClose}
        size="sm"
      >
        <ModalOverlay />
        <ModalContent pb={3} overflow="visible">
          <ModalHeader>فیلتر تاریخ گردش حساب</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody py={4} overflow="visible">
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>از تاریخ</FormLabel>
                <PersianDatePicker
                  value={draftFromDate}
                  onChange={setDraftFromDate}
                  placeholder="تاریخ شروع"
                />
              </FormControl>
              <FormControl>
                <FormLabel>تا تاریخ</FormLabel>
                <PersianDatePicker
                  value={draftToDate}
                  onChange={setDraftToDate}
                  placeholder="تاریخ پایان"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <VStack spacing={3} w="100%">
              <Button
                colorScheme="brand"
                onClick={applyDateFilters}
                isDisabled={!canApplyDateFilters}
                w="100%"
                borderRadius={"xl"}
              >
                اعمال
              </Button>
              <Button
                variant="outline"
                onClick={clearDateFilters}
                w="100%"
                borderRadius={"xl"}
              >
                حذف فیلتر
              </Button>
            </VStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <RecordDetailsModal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title="جزئیات گردش حساب"
        data={selectedItem}
      />
    </>
  );

  return (
    <VStack align="stretch" spacing={5} w="100%" h="100%">
      <CrudPage
        title={
          selectedFundData
            ? `گردش حساب ${selectedFundData.fundName.trim()}`
            : "گردش حساب"
        }
        columns={turnoverColumns}
        queryKey="turnoverAccount"
        resourceName="turnoverAccount"
        api={{
          getAll: (page, size) =>
            tadbir.getTurnoverAccount({
              fundSeoRegisterNo: selectedFund,
              fromDate: appliedFromDate,
              toDate: appliedToDate,
              pageNumber: page,
              pageSize: size,
              sortField: "voucherDate",
              sortDirection: "desc",
            }),
        }}
        FormComponent={() => null}
        showAddButton={false}
        enableEdit={false}
        enableDelete={false}
        enableView={false}
        pageSize={pageSize}
        queryDependencies={[selectedFund, appliedFromDate, appliedToDate]}
        queryEnabled={!!selectedFund && !!identifier}
        keepPreviousData={false}
        beforeTable={headerContent}
        afterTable={footerContent}
        errorMessage="دریافت گردش حساب با خطا مواجه شد."
        onDataLoaded={(queryData) =>
          setTurnoverRows(queryData?.turnoverAccount || [])
        }
      />
    </VStack>
  );
};
